import React, { useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import CategoryDropdown from "../components/CategoryDropdown";
import { categoryTypeQuestion } from "../constants/questionTypes";

const CategoryQuestion = (props) => {
  const {
    saveUpdatedAnswers,
    currentQuestionDet,   // sequence node: has category_question_id and maybe response.response
    question,             // fallback question text
    description,          // fallback description
    categoryQues,         // full categories array from API
    language,
  } = props;

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryQues, setSelectedCategoryQues] = useState(null);

  // find the category for this step
  useEffect(() => {
    if (!categoryQues || !currentQuestionDet?.category_question_id) {
      setSelectedCategoryQues(null);
      return;
    }
    const targetId = String(currentQuestionDet.category_question_id);
    const match =
      categoryQues.find((c) => String(c.id) === targetId) || null;
    setSelectedCategoryQues(match);
  }, [categoryQues, currentQuestionDet]);

  // localize options for display without mutating the source
  const localizedOptions = useMemo(() => {
    const opts = selectedCategoryQues?.options || [];
    return opts.map((op) => ({
      ...op,
      // CustomDropdown expects a `name` string; show FR if provided
      name: language === "FR" && op.fr ? op.fr : op.name,
    }));
  }, [selectedCategoryQues, language]);

  // preselect if the sequence node already has a response
  useEffect(() => {
    const respId = currentQuestionDet?.response?.response;
    if (respId && localizedOptions.length) {
      const preset =
        localizedOptions.find((op) => String(op.id) === String(respId)) ||
        null;
      setSelectedCategory(preset);
    } else {
      setSelectedCategory(null);
    }
  }, [currentQuestionDet, localizedOptions]);

  function SaveResponse(optionObj) {
    if (!optionObj || !selectedCategoryQues) return;

    const Response = {
      ques_sort_order: currentQuestionDet?.id,     // step id in sequence
      id: selectedCategoryQues.id,                 // category id (UUID)
      response: optionObj.id,                      // chosen option id (UUID)
      subfactor: selectedCategoryQues?.subfactor,  // (likely undefined—kept to preserve shape)
      priority: selectedCategoryQues?.priority,
      role: selectedCategoryQues?.role,
      reverse: selectedCategoryQues?.reverse,
      clicked: true,
    };

    saveUpdatedAnswers(currentQuestionDet?.id, Response, categoryTypeQuestion);
    setSelectedCategory(optionObj);
  }

  // Friendly text fallbacks
  const titleText = selectedCategoryQues?.name || question || "Demographic Question";
  const descText =
    (language === "FR"
      ? selectedCategoryQues?.fr
      : selectedCategoryQues?.question) || description || "";

  return (
    <CategoriesContainer>
      {descText ? (
        <>
          <QuestionText>{titleText}</QuestionText>
          <Description questionLength={descText.length}>{descText}</Description>
        </>
      ) : (
        <>
          <Title>Demographic Question</Title>
          <QuestionText>{titleText}</QuestionText>
        </>
      )}

      {localizedOptions.length > 0 && (
        <CategoryDropdown
          id={`sel_${currentQuestionDet?.id}`}
          listId={`lists_${currentQuestionDet?.id}`}
          value={selectedCategory}            // expects the selected option object
          onSelect={SaveResponse}            // receives the option object
          options={localizedOptions}         // array of {id, name, ...}
        />
      )}
    </CategoriesContainer>
  );
};

export default CategoryQuestion;

const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  max-width: 600px;
`;

const QuestionText = styled.h2`
  margin-bottom: 1rem;
  color: #2a3039;
  font-size: 20px;
  font-weight: 600;
`;

const Title = styled.div`
  color: #2d70e2;
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 600;
`;

const Description = styled.div`
  color: #2a3039;
  font-family: "Raleway";
  font-style: normal;
  font-weight: 400;
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 26px;

  @media (max-height: 700px) {
    font-size: ${(props) => (props.questionLength > 200 ? "14px" : "16px")};
  }
  @media (max-height: 550px) {
    font-size: ${(props) => (props.questionLength > 200 ? "12px" : "16px")};
  }
`;
